import { TimeFormatPreference } from "@api/graphql/generated/generated-types";
import { InputProps } from "baseui/input";
import React, { useState } from "react";
import Input from "../Input/Input";
import { useTimeFormat } from "../TimeFormatProvider/TimeFormatProvider";
import { formatTimeValue, parseTimeInput, sanitizeDraft } from "./timeMask";

/** baseui's Input types its handlers for both element kinds it can render. */
type TimeInputElement = HTMLInputElement | HTMLTextAreaElement;

export type TimeFormat = "12" | "24";

export interface TimePickerProps extends Omit<InputProps, "value" | "onChange" | "type"> {
  /** Canonical 24-hour "HH:mm". Empty string clears the field. */
  value?: string | null;
  /**
   * Receives a change-like event whose target.value is 24-hour "HH:mm" (or ""
   * when cleared), in both formats.
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** Overrides the courier's preference. Mirrors baseui/timepicker's prop. */
  format?: TimeFormat;
  /**
   * @deprecated No-op, kept so existing call sites keep compiling. It was
   * copied from baseui/timepicker and never did anything here; clearing the
   * field already reports an empty string.
   */
  nullable?: boolean;
  className?: string;
  id?: string;
}

/**
 * Time entry that always speaks 24-hour "HH:mm".
 *
 * A courier on 12-hour time keeps the native <input type="time">: it renders
 * and edits correctly on the 12-hour-locale browsers those couriers run, and it
 * brings segment editing, arrow-key stepping and the platform picker with it.
 *
 * A courier on 24-hour time gets a masked text input instead. The native
 * control takes its 12h/24h behavior from the browser/OS locale with no way to
 * override it, so on a 12-hour-locale browser its hour segment accepts only
 * 1-12 — which made hours 20-23 unenterable for couriers running on military
 * time (CRI-100). The mask is locale-independent by construction: "2100" is
 * 21:00 on every machine.
 *
 * Known gap: a 12-hour courier whose browser locale is 24-hour still sees a
 * 24-hour native control. That mismatch is display-only — the value stored is
 * correct either way — and predates this component.
 */
function TimePicker({
  value,
  onChange,
  format,
  onBlur,
  onFocus,
  nullable: _nullable,
  ...rest
}: TimePickerProps) {
  const { timeFormatMode } = useTimeFormat();
  const resolvedFormat: TimeFormat =
    format ?? (timeFormatMode === TimeFormatPreference.TwelveHour ? "12" : "24");

  // What has been typed since focus, colon included so an edited value keeps
  // its hour/minute split. Null means nothing typed yet, so the field still
  // shows the formatted value.
  const [draft, setDraft] = useState<string | null>(null);

  function emit(nextValue: string, event: React.SyntheticEvent<TimeInputElement>) {
    if (!onChange || nextValue === (value ?? "")) return;

    const target = { ...event.target, name: rest.name, value: nextValue };
    onChange({
      ...event,
      target,
      currentTarget: target,
      type: "change",
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  }

  function handleChange(event: React.ChangeEvent<TimeInputElement>) {
    const nextDraft = sanitizeDraft(event.target.value);
    setDraft(nextDraft);
    emit(parseTimeInput(nextDraft) ?? "", event);
  }

  // Select on focus so typing replaces the time rather than inserting into it.
  function handleFocus(event: React.FocusEvent<TimeInputElement>) {
    event.target.select();
    onFocus?.(event);
  }

  function handleBlur(event: React.FocusEvent<TimeInputElement>) {
    if (draft !== null) emit(parseTimeInput(draft) ?? "", event);
    setDraft(null);
    onBlur?.(event);
  }

  if (resolvedFormat === "12") {
    return (
      <Input
        {...rest}
        type="time"
        value={value ?? ""}
        onChange={onChange as InputProps["onChange"]}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    );
  }

  return (
    <Input
      {...rest}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder={rest.placeholder ?? "HH:mm"}
      value={draft ?? formatTimeValue(value)}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
}

export { TimePicker };
